import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../store/hooks';
import {
  fetchStudentByIdAsync,
  updateStudentAsync,
  clearError,
  clearCurrentStudent,
  fetchStudentVacationsAsync,
  fetchStudentSubscriptionsAsync,
  moveStudentGroupAsync,
  flagStudentRiskAsync,
  clearStudentRiskAsync,
  deleteFreezeAsync,
  assignIndividualOfferAsync,
  type StudentSubscription,
} from '../store/slices/studentsSlice';
import { fetchGroupsAsync } from '../store/slices/groupsSlice';
import LoadingSpinner from './LoadingSpinner';
import ConfirmationModal from './ConfirmationModal';
import FreezeModal from './FreezeModal';
import RewardSubscriptionBox from './RewardSubscriptionBox';
import StudentReferrerPicker, { formatStudentName, type ReferrerOption } from './StudentReferrerPicker';
import Select, { type SelectOption } from './Select';
import { formatDateEuropean } from '../utils/dateUtils';
import './GroupForm.css';
import InfoTip from './InfoTip';
import './RewardSubscriptionBox.css';

const EditStudent: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { currentStudent, loading, error, studentVacations, loadingVacations, studentSubscriptions, loadingSubscriptions } = useAppSelector((state) => state.students);
  const { groups } = useAppSelector((state) => state.groups);

  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    surname: '',
    groupId: '',
    contacts: '',
    startDate: '',
    quitted: false,
    quitDate: '',
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const groupsLoadedRef = useRef(false);
  const formInitializedRef = useRef(false);

  // Move-to-another-group modal
  const [showMoveModal, setShowMoveModal] = useState(false);
  const [moveToGroup, setMoveToGroup] = useState<SelectOption<number> | null>(null);
  const [moveDate, setMoveDate] = useState('');
  const [moving, setMoving] = useState(false);
  const [moveError, setMoveError] = useState<string | null>(null);

  // Freeze ("paid vacation") modal + delete confirmation
  const [showFreezeModal, setShowFreezeModal] = useState(false);
  const [editingFreeze, setEditingFreeze] = useState<StudentSubscription | null>(null);
  const [freezeToDelete, setFreezeToDelete] = useState<StudentSubscription | null>(null);
  const [deletingFreeze, setDeletingFreeze] = useState(false);

  const [referrerOption, setReferrerOption] = useState<ReferrerOption | null>(null);
  const [editingReward, setEditingReward] = useState<StudentSubscription | null>(null);

  // Churn-risk modal
  const [showRiskModal, setShowRiskModal] = useState(false);
  const [riskLevel, setRiskLevel] = useState<'moderate' | 'certain'>('moderate');
  const [riskDate, setRiskDate] = useState('');
  const [riskNote, setRiskNote] = useState('');
  const [savingRisk, setSavingRisk] = useState(false);
  const [riskError, setRiskError] = useState<string | null>(null);

  // Individual-offer confirmation (rare, legacy concept — kept deliberately low-key)
  const [showOfferConfirm, setShowOfferConfirm] = useState(false);
  const [assigningOffer, setAssigningOffer] = useState(false);
  const [offerError, setOfferError] = useState<string | null>(null);


  // Load student data when ID changes
  useEffect(() => {
    if (id) {
      const studentId = parseInt(id);
      dispatch(fetchStudentByIdAsync(studentId));
      dispatch(fetchStudentVacationsAsync(studentId));
      dispatch(fetchStudentSubscriptionsAsync(studentId));
      formInitializedRef.current = false;
    }

    return () => {
      dispatch(clearCurrentStudent());
    };
  }, [dispatch, id]);

  // Load groups only once
  useEffect(() => {
    if (!groupsLoadedRef.current) {
      dispatch(fetchGroupsAsync({ page: 1, limit: 100 }));
      groupsLoadedRef.current = true;
    }
  }, [dispatch]);

  useEffect(() => {
    if (currentStudent && !formInitializedRef.current) {
      // Get the first group from the groups array (students can now belong to multiple groups)
      const firstGroup = currentStudent.groups && currentStudent.groups.length > 0 
        ? currentStudent.groups[0] 
        : null;
      
      // A quit_date in the future is a *scheduled* quit (the student is still active
      // until then) — don't pre-check the "has quitted" box for it.
      const quitDateStr = currentStudent.quitDate ? currentStudent.quitDate.split('T')[0] : '';
      const todayStr = new Date().toISOString().split('T')[0];
      const actuallyQuit = !!quitDateStr && quitDateStr <= todayStr;

      setFormData({
        firstName: currentStudent.firstName || '',
        lastName: currentStudent.lastName || '',
        surname: currentStudent.surname || '',
        groupId: firstGroup?.id?.toString() || '',
        contacts: currentStudent.contacts || '',
        startDate: currentStudent.startDate ? currentStudent.startDate.split('T')[0] : '',
        quitted: actuallyQuit,
        quitDate: quitDateStr,
      });
      if (currentStudent.referrer) {
        setReferrerOption({
          value: currentStudent.referrer.id,
          label: formatStudentName(currentStudent.referrer),
          student: currentStudent.referrer,
        });
      } else {
        setReferrerOption(null);
      }
      formInitializedRef.current = true;
    }
  }, [currentStudent]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = (e.target as HTMLInputElement).checked;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear form error when user starts typing
    if (formErrors[name]) {
      setFormErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    
    // Auto-fill today's date when checking the quitted checkbox if no date exists
    if (name === 'quitted' && checked && !formData.quitDate) {
      const today = new Date().toISOString().split('T')[0];
      setFormData(prev => ({
        ...prev,
        quitDate: today
      }));
    }
    
    // Don't clear quit date when unchecking - preserve the value until form submission
  };

  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.firstName.trim()) {
      errors.firstName = 'First name is required';
    }

    if (!formData.lastName.trim()) {
      errors.lastName = 'Last name is required';
    }

    if (!formData.startDate) {
      errors.startDate = 'First payment date is required';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm() || !currentStudent) {
      return;
    }

    const updateData = {
      firstName: formData.firstName.trim(),
      lastName: formData.lastName.trim(),
      surname: formData.surname.trim() || undefined,
      contacts: formData.contacts.trim() || undefined,
      startDate: formData.startDate,
      quitDate: formData.quitted && formData.quitDate ? formData.quitDate : undefined,
      // Record who brought this student right away; the gift itself is granted
      // later, once they have paid their first month.
      ...(currentStudent.referredByStudentId
        ? {}
        : { referredByStudentId: referrerOption ? referrerOption.value : null }),
    };

    try {
      await dispatch(updateStudentAsync({ 
        id: currentStudent.id, 
        studentData: updateData 
      })).unwrap();
      navigate('/students');
    } catch (error) {
      // Error is handled by the slice
    }
  };

  const handleCancel = () => {
    navigate('/students');
  };

  const currentGroup = currentStudent?.groups && currentStudent.groups.length > 0
    ? currentStudent.groups[0]
    : null;
  const mentionEligibility = currentStudent?.mentionEligibility;
  const mentionSubscriptions = studentSubscriptions.filter(
    (sub) => sub.subscriptionType?.rewardKind === 'mention'
  );
  const latestMention = mentionSubscriptions.reduce<StudentSubscription | null>((latest, mention) => {
    if (!latest) return mention;
    const mentionTime = mention.createdAt ? new Date(mention.createdAt).getTime() : mention.id;
    const latestTime = latest.createdAt ? new Date(latest.createdAt).getTime() : latest.id;
    return mentionTime > latestTime ? mention : latest;
  }, null);
  const nonMentionSubscriptions = studentSubscriptions.filter(
    (sub) => sub.subscriptionType?.rewardKind !== 'mention'
  );

  const groupOptions: SelectOption<number>[] = groups
    .filter((g) => g.id !== currentGroup?.id)
    .map((g) => ({ value: g.id, label: g.name || `${g.level.toUpperCase()} Group` }));

  const openMoveModal = () => {
    setMoveToGroup(null);
    setMoveDate(new Date().toISOString().split('T')[0]);
    setMoveError(null);
    setShowMoveModal(true);
  };

  const handleConfirmMove = async () => {
    if (!currentStudent || !moveToGroup) return;
    setMoving(true);
    setMoveError(null);
    try {
      await dispatch(moveStudentGroupAsync({
        studentId: currentStudent.id,
        toGroupId: moveToGroup.value,
        movedOn: moveDate || undefined,
      })).unwrap();
      setShowMoveModal(false);
      // Refresh student + subscriptions (end dates recompute against the new group's schedule)
      dispatch(fetchStudentByIdAsync(currentStudent.id));
      dispatch(fetchStudentSubscriptionsAsync(currentStudent.id));
    } catch (err) {
      setMoveError(typeof err === 'string' ? err : 'Failed to move student');
    } finally {
      setMoving(false);
    }
  };

  const openCreateFreeze = () => {
    setEditingFreeze(null);
    setShowFreezeModal(true);
  };

  const openEditFreeze = (sub: StudentSubscription) => {
    setEditingFreeze(sub);
    setShowFreezeModal(true);
  };

  const handleFreezeSaved = () => {
    if (currentStudent) dispatch(fetchStudentSubscriptionsAsync(currentStudent.id));
  };

  const handleConfirmDeleteFreeze = async () => {
    if (!currentStudent || !freezeToDelete) return;
    setDeletingFreeze(true);
    try {
      await dispatch(deleteFreezeAsync({ studentId: currentStudent.id, subscriptionId: freezeToDelete.id })).unwrap();
      setFreezeToDelete(null);
    } catch {
      // error surfaced via slice state
    } finally {
      setDeletingFreeze(false);
    }
  };

  const openRiskModal = () => {
    if (!currentStudent) return;
    setRiskLevel(currentStudent.riskLevel || 'moderate');
    // Default the potential fall-out date to the student's estimated subscription end.
    const fallback = currentStudent.riskPotentialEndDate || currentStudent.subExpEstimate || '';
    setRiskDate(fallback ? fallback.split('T')[0] : '');
    setRiskNote(currentStudent.riskNote || '');
    setRiskError(null);
    setShowRiskModal(true);
  };

  const handleConfirmAssignOffer = async () => {
    if (!currentStudent) return;
    setAssigningOffer(true);
    setOfferError(null);
    try {
      await dispatch(assignIndividualOfferAsync(currentStudent.id)).unwrap();
      setShowOfferConfirm(false);
    } catch (err) {
      setOfferError(typeof err === 'string' ? err : 'Failed to assign individual offer');
    } finally {
      setAssigningOffer(false);
    }
  };

  const handleConfirmRisk = async () => {
    if (!currentStudent) return;
    setSavingRisk(true);
    setRiskError(null);
    try {
      await dispatch(flagStudentRiskAsync({
        id: currentStudent.id,
        riskLevel,
        potentialEndDate: riskDate || null,
        note: riskNote.trim() || null,
      })).unwrap();
      setShowRiskModal(false);
    } catch (err) {
      setRiskError(typeof err === 'string' ? err : 'Failed to mark at risk');
    } finally {
      setSavingRisk(false);
    }
  };

  const handleClearRisk = async () => {
    if (!currentStudent) return;
    try {
      await dispatch(clearStudentRiskAsync({ id: currentStudent.id })).unwrap();
    } catch {
      // error surfaced via slice state
    }
  };

  if (loading && !currentStudent) {
    return <LoadingSpinner />;
  }

  if (!currentStudent && !loading) {
    return (
      <div className="group-form-container">
        <div className="page-header">
          <div className="page-header-content">
            <h1>Student Not Found</h1>
            <p>The student you're looking for doesn't exist or has been deleted.</p>
          </div>
          <button 
            type="button" 
            className="btn-secondary"
            onClick={handleCancel}
          >
            Back to Students
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="group-form-container">
      <div className="page-header">
        <div className="page-header-content">
          <h1>Edit Student</h1>
          <p>Update student information and enrollment details</p>
        </div>
        <button 
          type="button" 
          className="btn-secondary"
          onClick={handleCancel}
        >
          Cancel
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => dispatch(clearError())} className="close-error">×</button>
        </div>
      )}

      <form onSubmit={handleSubmit} className="group-form">
        <div className="form-section">
          <h2>Student Information</h2>
          
          <div className="form-row-compact three-columns">
            <div className="form-group">
              <label htmlFor="firstName">First Name *</label>
              <input
                type="text"
                id="firstName"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                className={`form-input ${formErrors.firstName ? 'error' : ''}`}
                required
              />
              {formErrors.firstName && <span className="error-text">{formErrors.firstName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="lastName">Last Name *</label>
              <input
                type="text"
                id="lastName"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                className={`form-input ${formErrors.lastName ? 'error' : ''}`}
                required
              />
              {formErrors.lastName && <span className="error-text">{formErrors.lastName}</span>}
            </div>

            <div className="form-group">
              <label htmlFor="surname">Surname</label>
              <input
                type="text"
                id="surname"
                name="surname"
                value={formData.surname}
                onChange={handleInputChange}
                className="form-input"
                placeholder="Enter surname (optional)"
              />
            </div>
          </div>

          <div className="form-group">
            <div className="label-with-info">
              <label>Group</label>
              <InfoTip text="Moving the student recomputes their subscription end dates against the new group's schedule." />
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap' }}>
              <input
                type="text"
                className="form-input"
                style={{ maxWidth: 280 }}
                value={currentGroup ? (currentGroup.name || `${currentGroup.level.toUpperCase()} Group`) : 'No group'}
                disabled
              />
              <button type="button" className="btn-secondary" onClick={openMoveModal}>
                Move to another group
              </button>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="contacts">Contacts</label>
            <textarea
              id="contacts"
              name="contacts"
              value={formData.contacts}
              onChange={handleInputChange}
              className="form-input"
              placeholder="Enter contact information (phone, email, etc.)"
              rows={3}
            />
          </div>

          <div className="form-group">
            <label htmlFor="startDate">First Payment Date *</label>
            <input
              type="date"
              id="startDate"
              name="startDate"
              value={formData.startDate}
              onChange={handleInputChange}
              className={`form-input ${formErrors.startDate ? 'error' : ''}`}
              required
            />
            {formErrors.startDate && <span className="error-text">{formErrors.startDate}</span>}
          </div>

          <div className="form-group">
            <div className="label-with-info">
              <label>Brought by</label>
              <InfoTip text="The student who recommended this person. Their 4 free lessons unlock once this student pays their first month." />
            </div>
            <StudentReferrerPicker
              value={referrerOption}
              onChange={setReferrerOption}
              excludeStudentId={currentStudent?.id}
              disabled={Boolean(currentStudent?.referredByStudentId)}
            />
          </div>
        </div>

        <div className="form-section">
          <h2>Status</h2>
          
          <div className="student-item">
            <div className="student-info">
              <div className="student-status">
                {formData.quitted && formData.quitDate ? (
                  <span className="status-quit">
                    Quit ({formatDateEuropean(formData.quitDate)})
                  </span>
                ) : formData.quitted ? (
                  <span className="status-quit">
                    Quit (Pending)
                  </span>
                ) : (
                  <span className="status-active">Active</span>
                )}
              </div>

              <div className="student-quit-section">
                <label className="checkbox-control">
                  <input
                    type="checkbox"
                    name="quitted"
                    checked={formData.quitted}
                    onChange={handleInputChange}
                  />
                  <span className="checkbox-control__label">Student has quitted</span>
                </label>

                {formData.quitted && (
                  <div className="form-group quit-date-input">
                    <label htmlFor="quitDate">Quit date</label>
                    <input
                      type="date"
                      id="quitDate"
                      name="quitDate"
                      value={formData.quitDate}
                      onChange={handleInputChange}
                      className="form-input"
                    />
                  </div>
                )}
              </div>

              {/* Churn-risk: low-key control. Hidden once the student has quit. */}
              {!formData.quitted && (
                <div className="risk-control">
                  {currentStudent?.atRisk ? (
                    <div className="risk-control__active">
                      <span className={`risk-dot risk-dot--${currentStudent.riskLevel === 'certain' ? 'certain' : 'moderate'}`} />
                      <span className="risk-control__label">
                        At risk ({currentStudent.riskLevel === 'certain' ? 'certain' : 'moderate'})
                        {currentStudent.riskMarkedAt ? ` since ${formatDateEuropean(currentStudent.riskMarkedAt)}` : ''}
                        {currentStudent.quitDate && currentStudent.quitDate.split('T')[0] > new Date().toISOString().split('T')[0]
                          ? ` · auto-quit ${formatDateEuropean(currentStudent.quitDate)}`
                          : currentStudent.riskPotentialEndDate ? ` · est. leave ${formatDateEuropean(currentStudent.riskPotentialEndDate)}` : ''}
                      </span>
                      <button type="button" className="btn-outline-sm" onClick={openRiskModal}>Edit</button>
                      <button type="button" className="btn-outline-sm" onClick={handleClearRisk}>Clear risk</button>
                    </div>
                  ) : (
                    <button type="button" className="btn-outline-sm" onClick={openRiskModal}>
                      Mark at risk of leaving
                    </button>
                  )}
                </div>
              )}

              {/* Individual offer: legacy/rare concept, manual-only and deliberately
                  low-key (docs/specs/offer-number-assignment-and-export.md) */}
              <div className="risk-control">
                {currentStudent?.individualOfferRef ? (
                  <span className="risk-control__label">
                    Individual offer №{currentStudent.individualOfferRef.number}
                    {' · '}{formatDateEuropean(currentStudent.individualOfferRef.date)}
                  </span>
                ) : (
                  <button type="button" className="btn-outline-sm" onClick={() => setShowOfferConfirm(true)}>
                    Assign individual offer
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="form-actions">
          <button
            type="button"
            onClick={handleCancel}
            className="btn-secondary"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="btn-success"
            disabled={loading}
          >
            {loading ? 'Updating...' : 'Update Student'}
          </button>
        </div>
      </form>

      {/* Subscriptions Section */}
      {currentStudent && (
        <div className="detail-block">
          <div className="detail-block-header">
            <h3>Subscriptions</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-outline-sm"
                onClick={openCreateFreeze}
              >
                ❄ Freeze
              </button>
              <button
                type="button"
                className="btn-outline-sm"
                onClick={() => navigate('/subscriptions/create')}
              >
                + Add Subscription
              </button>
            </div>
          </div>

          {currentStudent.referralReward && !editingReward && (
            <div className="reward-compact">
              Brought by{' '}
              <a href={`/students/edit/${currentStudent.referralReward.studentId}`}>
                {currentStudent.referrer ? formatStudentName(currentStudent.referrer) : 'referrer'}
              </a>
              {currentStudent.referralReward.proofUrl && (
                <a href={currentStudent.referralReward.proofUrl} target="_blank" rel="noreferrer">View proof</a>
              )}
            </div>
          )}

          {currentStudent.referralRewardAwaitingPayment && !editingReward && (
            <div className="reward-pending">
              Brought by{' '}
              {currentStudent.referrer ? (
                <a href={`/students/edit/${currentStudent.referrer.id}`}>
                  {formatStudentName(currentStudent.referrer)}
                </a>
              ) : (
                'referrer'
              )}
              <span className="reward-pending__hint">
                ↳ 4-lesson gift unlocks on first payment
              </span>
            </div>
          )}

          {(referrerOption && !editingReward && currentStudent.pendingReferralReward) && (
            <RewardSubscriptionBox
              kind="referral"
              recipientStudentId={referrerOption.value}
              recipientName={formatStudentName(referrerOption.student)}
              referredStudentId={currentStudent.id}
              referredFirstName={currentStudent.firstName || undefined}
              onSuccess={() => {
                setEditingReward(null);
                dispatch(fetchStudentByIdAsync(currentStudent.id));
                dispatch(fetchStudentSubscriptionsAsync(currentStudent.id));
              }}
            />
          )}

          {editingReward?.subscriptionType?.rewardKind === 'referral' && editingReward.referredStudentId && (
            <RewardSubscriptionBox
              kind="referral"
              recipientStudentId={currentStudent.id}
              recipientName={formatStudentName(currentStudent)}
              referredStudentId={editingReward.referredStudentId}
              referredFirstName={editingReward.referredStudent?.firstName || undefined}
              existing={editingReward}
              onDismiss={() => setEditingReward(null)}
              onSuccess={() => {
                setEditingReward(null);
                dispatch(fetchStudentByIdAsync(currentStudent.id));
                dispatch(fetchStudentSubscriptionsAsync(currentStudent.id));
              }}
            />
          )}

          {loadingSubscriptions ? (
            <div className="loading-text">Loading subscriptions...</div>
          ) : nonMentionSubscriptions.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No subscriptions for this student.</p>
          ) : (
            <div className="students-table-scroll">
              <table className="students-table" style={{ marginTop: '0.5rem' }}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Start</th>
                    <th>Payment</th>
                    <th>Lessons</th>
                    <th>Price</th>
                    <th>Revenue</th>
                    <th>End</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {nonMentionSubscriptions.map((sub) => {
                    const end = sub.effectiveEndDate ?? sub.endDate ?? sub.estimatedEndDate;
                    const isFreeze = !!sub.subscriptionType?.isFreeze;
                    const isReward = !!sub.subscriptionType?.isReward;
                    const rewardKind = sub.subscriptionType?.rewardKind;
                    return (
                    <tr key={sub.id}>
                      <td>
                        <strong>
                          {isFreeze
                            ? `❄ ${sub.subscriptionType?.name || 'Заморозка'}`
                            : isReward
                              ? sub.subscriptionType?.name || 'Reward'
                              : (sub.subscriptionType?.name || '—')}
                        </strong>
                        {rewardKind === 'referral' && sub.referredStudent && (
                          <div style={{ fontSize: '12px', color: '#6b7280', fontWeight: 400 }}>
                            for {formatStudentName(sub.referredStudent)}
                          </div>
                        )}
                      </td>
                      <td>{formatDateEuropean(sub.startDate)}</td>
                      <td>{sub.paymentDate ? formatDateEuropean(sub.paymentDate) : '—'}</td>
                      <td>{isFreeze ? '—' : sub.lessonsCount}</td>
                      <td>{isFreeze || isReward ? '—' : parseFloat(sub.pricePerLesson).toFixed(2)}</td>
                      <td>{isReward ? '0 BYN' : `${sub.revenue || sub.totalAmount || '—'} BYN`}</td>
                      <td>{end ? formatDateEuropean(end) : '—'}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            onClick={() => {
                              if (isFreeze) openEditFreeze(sub);
                              else if (isReward) {
                                setEditingReward(sub);
                              } else {
                                navigate(`/subscriptions/edit/${currentStudent.id}/${sub.id}`);
                              }
                            }}
                            className="btn-icon"
                            data-tooltip="Edit"
                          >
                            ✎
                          </button>
                          {sub.proofUrl && (
                            <a
                              href={sub.proofUrl}
                              target="_blank"
                              rel="noreferrer"
                              className="btn-icon"
                              data-tooltip="View proof"
                            >
                              🖼
                            </a>
                          )}
                          {isFreeze && (
                            <button
                              type="button"
                              onClick={() => setFreezeToDelete(sub)}
                              className="btn-icon"
                              data-tooltip="Delete freeze"
                            >
                              🗑
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Vacations Section */}
      {currentStudent && (
        <div className="detail-block">
          <div className="detail-block-header">
            <h3>Vacations</h3>
            <button
              type="button"
              className="btn-outline-sm"
              onClick={() => navigate(`/student-vacations/create?studentId=${currentStudent.id}`)}
            >
              + Add Vacation
            </button>
          </div>

          {loadingVacations ? (
            <div className="loading-text">Loading vacations...</div>
          ) : studentVacations.length === 0 ? (
            <p style={{ color: '#6b7280' }}>No vacation records for this student.</p>
          ) : (
            <div className="students-table-scroll">
              <table className="students-table" style={{ marginTop: '0.5rem' }}>
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>From</th>
                    <th>To</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {studentVacations.map(vacation => (
                    <tr key={vacation.id}>
                      <td><strong>{vacation.vacationType?.name || '—'}</strong></td>
                      <td>{formatDateEuropean(vacation.dateFrom)}</td>
                      <td>{formatDateEuropean(vacation.dateTo)}</td>
                      <td>
                        <div className="action-buttons">
                          <button
                            type="button"
                            onClick={() => navigate(`/student-vacations/edit/${currentStudent.id}/${vacation.id}`)}
                            className="btn-icon"
                            data-tooltip="Edit"
                          >
                            ✎
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* Mentions Section */}
      {currentStudent && (
        <div className="detail-block">
          <div className="detail-block-header">
            <h3>Mentions</h3>
            <div style={{ display: 'flex', gap: '0.5rem' }}>
              <button
                type="button"
                className="btn-outline-sm"
                onClick={() => navigate(`/mentions?studentId=${currentStudent.id}`)}
              >
                Full history
              </button>
              {mentionEligibility?.status === 'allowed' && (
                <button
                  type="button"
                  className="btn-outline-sm"
                  onClick={() => navigate(`/mentions/create?studentId=${currentStudent.id}`)}
                >
                  + Add Mention
                </button>
              )}
            </div>
          </div>

          {mentionEligibility && (
            <div className={`mention-eligibility mention-eligibility--${mentionEligibility.status}`}>
              {mentionEligibility.status === 'allowed' && mentionEligibility.giveaway && (
                <>
                  <strong>Eligible for a new mention.</strong>{' '}
                  Giveaway: {mentionEligibility.giveaway.name}. Mentions accepted until{' '}
                  {formatDateEuropean(mentionEligibility.giveaway.mentionUntil)}.
                </>
              )}
              {mentionEligibility.status === 'blocked' && mentionEligibility.reason === 'already_rewarded' && (
                <>
                  <strong>Already rewarded</strong>
                  {mentionEligibility.giveaway ? ` for ${mentionEligibility.giveaway.name}.` : '.'}
                </>
              )}
              {mentionEligibility.status === 'blocked' && mentionEligibility.reason !== 'already_rewarded' && (
                <>
                  <strong>Not in a running giveaway.</strong>{' '}
                  {mentionEligibility.reason === 'not_in_group'
                    ? 'This student is not in a group.'
                    : 'Mention rewards are only open to students in a giveaway’s target groups.'}
                </>
              )}
              {mentionEligibility.lastGrantedAt && (
                <span> Last grant: {formatDateEuropean(mentionEligibility.lastGrantedAt)}.</span>
              )}
            </div>
          )}

          {loadingSubscriptions ? (
            <div className="loading-text">Loading mentions...</div>
          ) : !latestMention ? (
            <p style={{ color: '#6b7280' }}>No mention rewards for this student.</p>
          ) : (
            <div className="mention-latest">
              <div>
                <strong>Last mention</strong>
                <span>
                  {latestMention.createdAt ? formatDateEuropean(latestMention.createdAt) : '—'}
                  {' · '}{latestMention.lessonsCount} lesson{latestMention.lessonsCount === 1 ? '' : 's'}
                </span>
              </div>
              <div className="mention-latest__actions">
                {latestMention.proofUrl && (
                  <a
                    href={latestMention.proofUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mention-action-link"
                  >
                    View proof
                  </a>
                )}
                <button
                  type="button"
                  className="btn-icon"
                  data-tooltip="Edit"
                  aria-label="Edit mention"
                  onClick={() => navigate(`/mentions/edit/${currentStudent.id}/${latestMention.id}`)}
                >
                  ✎
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      <ConfirmationModal
        isOpen={showMoveModal}
        title="Move to another group"
        message={`Move ${currentStudent ? `${currentStudent.firstName} ${currentStudent.lastName}` : 'student'} from ${currentGroup ? (currentGroup.name || currentGroup.level.toUpperCase()) : 'no group'} to:`}
        onConfirm={handleConfirmMove}
        onCancel={() => setShowMoveModal(false)}
        confirmText={moving ? 'Moving…' : 'Move'}
        confirmDisabled={!moveToGroup || moving}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#4a5568' }}>
              Destination group
            </label>
            <Select<number, SelectOption<number>>
              value={moveToGroup}
              onChange={setMoveToGroup}
              options={groupOptions}
              placeholder="Select a group…"
              noOptionsMessage="No other groups"
            />
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#4a5568' }}>
              Effective date
            </label>
            <input
              type="date"
              className="form-input"
              value={moveDate}
              onChange={(e) => setMoveDate(e.target.value)}
            />
          </div>
          {moveError && <span className="error-text">{moveError}</span>}
        </div>
      </ConfirmationModal>

      {currentStudent && (
        <FreezeModal
          isOpen={showFreezeModal}
          studentId={currentStudent.id}
          existing={editingFreeze}
          onClose={() => setShowFreezeModal(false)}
          onSaved={handleFreezeSaved}
        />
      )}

      <ConfirmationModal
        isOpen={!!freezeToDelete}
        title="Delete freeze"
        message="Remove this freeze and its parallel vacation period? This cannot be undone."
        onConfirm={handleConfirmDeleteFreeze}
        onCancel={() => setFreezeToDelete(null)}
        confirmText={deletingFreeze ? 'Deleting…' : 'Delete'}
        cancelText="Cancel"
        dangerous
        confirmDisabled={deletingFreeze}
      />

      <ConfirmationModal
        isOpen={showOfferConfirm}
        title="Assign individual offer"
        message={`Assign the next individual offer number to ${currentStudent ? `${currentStudent.firstName} ${currentStudent.lastName}` : 'this student'}? Offer numbers are permanent — they appear on accept certificates and cannot be unassigned from the UI.`}
        onConfirm={handleConfirmAssignOffer}
        onCancel={() => setShowOfferConfirm(false)}
        confirmText={assigningOffer ? 'Assigning…' : 'Assign'}
        confirmDisabled={assigningOffer}
      >
        {offerError && <span className="error-text">{offerError}</span>}
      </ConfirmationModal>

      <ConfirmationModal
        isOpen={showRiskModal}
        title={currentStudent?.atRisk ? 'Update risk' : 'Mark at risk of leaving'}
        message={`How likely is ${currentStudent ? `${currentStudent.firstName} ${currentStudent.lastName}` : 'this student'} to leave the class?`}
        onConfirm={handleConfirmRisk}
        onCancel={() => setShowRiskModal(false)}
        confirmText={savingRisk ? 'Saving…' : 'Save'}
        confirmDisabled={savingRisk}
      >
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginTop: '0.5rem' }}>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#4a5568' }}>
              Risk level
            </label>
            <div className="risk-seg" role="group" aria-label="Risk level">
              <button
                type="button"
                className={`risk-seg__btn ${riskLevel === 'moderate' ? 'risk-seg__btn--active' : ''}`}
                onClick={() => setRiskLevel('moderate')}
              >
                Moderate
              </button>
              <button
                type="button"
                className={`risk-seg__btn ${riskLevel === 'certain' ? 'risk-seg__btn--active' : ''}`}
                onClick={() => setRiskLevel('certain')}
              >
                Certain
              </button>
            </div>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#4a5568' }}>
              Potential leave date
            </label>
            <input
              type="date"
              className="form-input"
              value={riskDate}
              onChange={(e) => setRiskDate(e.target.value)}
            />
            <p style={{ fontSize: '0.8rem', color: '#6b7280', marginTop: '0.25rem' }}>
              {riskLevel === 'certain'
                ? 'Certain: the student is scheduled to quit on this date (must be on or after their subscription end). They stay active until then; a new payment cancels it.'
                : 'Defaults to the estimated subscription end — when the student is likely to fall out.'}
            </p>
          </div>
          <div>
            <label style={{ display: 'block', fontSize: '0.85rem', marginBottom: '0.25rem', color: '#4a5568' }}>
              Note (optional)
            </label>
            <textarea
              className="form-input"
              rows={2}
              value={riskNote}
              onChange={(e) => setRiskNote(e.target.value)}
              placeholder="Why is this student at risk?"
            />
          </div>
          {riskError && <span className="error-text">{riskError}</span>}
        </div>
      </ConfirmationModal>
    </div>
  );
};

export default EditStudent;